package com.bitterinfant.webui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

/**
 * A banner photo plus its [ImageBanner] placement settings, so a page can keep one per tab and
 * swap them as the active tab changes (see site-matthew/site-laura `bannerImages`).
 *
 * With [heightPercent] null the photo simply covers the whole banner. Otherwise it's pinned to
 * the right edge at that percentage of the banner's height (width follows the photo's aspect
 * ratio), [verticalPercent] places it vertically like a CSS background-position Y%, and its left
 * edge fades to transparent over [edgeFade]% of the photo's *own* width — so the fade follows the
 * photo's edge at every window size. On narrow windows the photo is capped at [maxWidthPercent]
 * of the banner and cropped around [focusX]% across the photo (keep the subject's face there).
 */
class BannerImage(
    val src: String,
    val heightPercent: Int? = null,
    val verticalPercent: Int = 50,
    val focusX: Int = 50,
    val maxWidthPercent: Int = 75,
    val edgeFade: Int = 30,
)

/**
 * A banner with a background photo and a left-to-right gradient overlay, so left-aligned
 * [content] stays readable while the photo shows through on the right. Until a real photo is
 * dropped at [image]'s src, the missing image is hidden and the banner shows a plain background.
 * Pass [flushTop] when the banner sits at the very top of the page, to square off its top corners.
 */
@Composable
fun ImageBanner(
    image: BannerImage,
    imageAlt: String,
    flushTop: Boolean = false,
    content: @Composable () -> Unit,
) {
    val pinned = image.heightPercent != null
    Div(attrs = {
        attr("role", "img")
        attr("aria-label", imageAlt)
        style {
            position(Position.Relative)
            property("border-radius", if (flushTop) "0 0 8px 8px" else "8px")
            property("overflow", "hidden")
            // A pinned photo fades into the uncovered strip, which must be exactly Theme.bg.
            property("background-color", if (pinned) Theme.bg else Theme.bgElevated)
            // The extra 56px (vs. a bare 260px) leaves room for the tab strip to overlap the
            // bottom of the photo — see the negative marginTop on the Tabs wrapper in Main.kt.
            minHeight(316.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
        }
    }) {
        // Keyed on src so a photo hidden after a load error comes back when the tab changes.
        key(image.src) {
            Img(src = image.src, alt = "", attrs = {
                attr("aria-hidden", "true")
                addEventListener("error") { (it.target as HTMLElement).style.display = "none" }
                style {
                    position(Position.Absolute)
                    property("object-fit", "cover")
                    val h = image.heightPercent
                    if (h == null) {
                        property("inset", "0")
                        width(100.percent)
                        height(100.percent)
                    } else {
                        property("right", "0")
                        property("top", "${(100 - h) * image.verticalPercent / 100.0}%")
                        height(h.percent)
                        property("width", "auto")
                        property("max-width", "${image.maxWidthPercent}%")
                        property("object-position", "${image.focusX}% ${image.verticalPercent}%")
                        val mask = "linear-gradient(90deg, transparent 0%, black ${image.edgeFade}%)"
                        property("mask-image", mask)
                        property("-webkit-mask-image", mask)
                    }
                }
            })
        }
        Div(attrs = {
            style {
                position(Position.Absolute)
                property("top", "0")
                property("left", "0")
                width(100.percent)
                height(100.percent)
                // Matches Theme.bg (#0b0b0d) as an rgba ramp so it can fade to transparent.
                property("background", "linear-gradient(90deg, ${Theme.bg} 0%, rgba(11,11,13,0.85) 45%, rgba(11,11,13,0.15) 80%, transparent 100%)")
            }
        })
        Div(attrs = {
            style {
                position(Position.Relative)
                maxWidth(560.px)
                // Left padding (1.2rem) matches the Tabs strip buttons' own horizontal padding
                // in Tabs.kt, so this content's left edge lines up with "Film"/"Samples"/
                // "Jed's Dead" text below instead of sitting further right than they do.
                property("padding", "2rem 2rem 2rem 1.2rem")
            }
        }) { content() }
    }
}
